import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { OrdersService } from "../../api/services/orders.service";

const normalizeAxonRequest = (payload) => {
  if (typeof payload === "object" && payload !== null) {
    return {
      axonId: payload.axonId,
      force: payload.force === true,
    };
  }

  return {
    axonId: payload,
    force: false,
  };
};

const initialState = {
  currentOrder: null,
  orderChanges: [],
  actionRequiredLogs: [],
  openOrdersSummaries: [],
  shouldRefreshOrders: false,
  isLoading: false,
  isLoadingLogs: false,
  logsError: null,
  logsFetchedForAxonId: null, // Track which axonId logs have been fetched for
  summariesFetchedForAxonId: null, // Track which axonId summaries have been fetched for
  error: null,
};

/** Async thunk to fetch order by ID from API */
export const fetchOrderById = createAsyncThunk(
  "orders/fetchById",
  async ({ orderId, roleId = "" }, { rejectWithValue }) => {
    try {
      const response = await OrdersService.getOrderById(orderId, roleId);
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch order");
    }
  },
);

/** Async thunk to fetch action required logs for an order */
export const fetchActionRequiredLogs = createAsyncThunk(
  "orders/fetchActionRequiredLogs",
  async (payload, { getState, rejectWithValue }) => {
    // async (axonId, { getState, rejectWithValue }) => {
    const { axonId, force } = normalizeAxonRequest(payload);
    // Check if logs are already fetched for this axonId
    const { logsFetchedForAxonId } = getState().orders;
    // if (logsFetchedForAxonId === axonId) {
    if (!force && logsFetchedForAxonId === axonId) {
      return null; // Already fetched, skip
    }

    try {
      const response = await OrdersService.getActionRequiredLogs(axonId);
      return { logs: response || [], axonId };
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to fetch action required logs",
      );
    }
  },
);

/** Async thunk to fetch open orders summaries for an order */
export const fetchOpenOrdersSummaries = createAsyncThunk(
  "orders/fetchOpenOrdersSummaries",
  async (payload, { getState, rejectWithValue }) => {
    // async (axonId, { getState, rejectWithValue }) => {
    const { axonId, force } = normalizeAxonRequest(payload);
    // Check if summaries are already fetched for this axonId
    const { summariesFetchedForAxonId } = getState().orders;
    // if (summariesFetchedForAxonId === axonId) {
    if (!force && summariesFetchedForAxonId === axonId) {
      return null; // Already fetched, skip
    }

    try {
      const response = await OrdersService.getOpenOrdersSummaries(axonId);
      return { summaries: response || [], axonId };
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to fetch open orders summaries",
      );
    }
  },
);

const ordersSlice = createSlice({
  name: "orders",
  initialState,
  reducers: {
    /** Sets current order directly (useful when order data is already available) */
    setCurrentOrder: (state, action) => {
      state.currentOrder = action.payload;
      state.error = null;
    },
    /** Sets order changes from notification */
    setOrderChanges: (state, action) => {
      state.orderChanges = action.payload || [];
    },
    /** Clears order changes */
    clearOrderChanges: (state) => {
      state.orderChanges = [];
    },
    /** Clears current order state */
    clearCurrentOrder: (state) => {
      state.currentOrder = null;
      state.orderChanges = [];
      state.actionRequiredLogs = [];
      state.openOrdersSummaries = [];
      state.logsFetchedForAxonId = null;
      state.summariesFetchedForAxonId = null;
      state.logsError = null;
      state.error = null;
      state.isLoading = false;
      state.isLoadingLogs = false;
    },
    /** Triggers a refresh of the orders list */
    triggerOrdersRefresh: (state) => {
      state.shouldRefreshOrders = true;
    },
    /** Clears the refresh flag after orders have been fetched */
    clearOrdersRefresh: (state) => {
      state.shouldRefreshOrders = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrderById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOrderById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentOrder = action.payload;
        state.error = null;
      })
      .addCase(fetchOrderById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Action required logs cases
      .addCase(fetchActionRequiredLogs.pending, (state) => {
        state.isLoadingLogs = true;
        state.logsError = null;
      })
      .addCase(fetchActionRequiredLogs.fulfilled, (state, action) => {
        state.isLoadingLogs = false;
        if (action.payload) {
          // Handle various response formats: nested data, array, or single object
          const logs =
            action.payload.logs?.data?.logs ||
            action.payload.logs?.data ||
            action.payload.logs ||
            [];
          // Normalize to array - handle both array and single object responses
          if (Array.isArray(logs)) {
            state.actionRequiredLogs = logs;
          } else if (logs && typeof logs === "object") {
            state.actionRequiredLogs = [logs];
          } else {
            state.actionRequiredLogs = [];
          }
          state.logsFetchedForAxonId = action.payload.axonId;
        }
        state.logsError = null;
      })
      .addCase(fetchActionRequiredLogs.rejected, (state, action) => {
        state.isLoadingLogs = false;
        state.logsError = action.payload;
        state.actionRequiredLogs = [];
      })
      // Open orders summaries cases
      .addCase(fetchOpenOrdersSummaries.pending, (state) => {
        state.isLoadingLogs = true;
        state.logsError = null;
      })
      .addCase(fetchOpenOrdersSummaries.fulfilled, (state, action) => {
        state.isLoadingLogs = false;
        if (action.payload) {
          // Handle various response formats
          const summaries =
            action.payload.summaries?.data?.logs ||
            action.payload.summaries?.data ||
            action.payload.summaries ||
            [];
          // Normalize to array
          if (Array.isArray(summaries)) {
            state.openOrdersSummaries = summaries;
          } else if (summaries && typeof summaries === "object") {
            state.openOrdersSummaries = [summaries];
          } else {
            state.openOrdersSummaries = [];
          }
          state.summariesFetchedForAxonId = action.payload.axonId;
        }
        state.logsError = null;
      })
      .addCase(fetchOpenOrdersSummaries.rejected, (state, action) => {
        state.isLoadingLogs = false;
        state.logsError = action.payload;
        state.openOrdersSummaries = [];
      });
  },
});

export const {
  setCurrentOrder,
  clearCurrentOrder,
  setOrderChanges,
  clearOrderChanges,
  triggerOrdersRefresh,
  clearOrdersRefresh,
} = ordersSlice.actions;

export default ordersSlice.reducer;
